import React, { useState, useEffect, useMemo } from 'react'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Checkbox } from 'components/ui/checkbox'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { GripVertical } from 'lucide-react'

interface DetailItem {
	_id: string
	detailLabel: string
	description: string
	detail: string
	detailType: string
}

interface BuildSiteDetailTemplateModalProps {
	details: DetailItem[]
	openPopup: boolean
	setOpenPopup: (open: boolean) => void
	onBuildTemplate: (data: any) => void
}

interface SortableRowProps {
	id: string
	seq: number
	detail: DetailItem
	isSelected: boolean
	isDisabled: boolean
	onSelect: (id: string, checked: boolean) => void
}

const SortableRow = ({
	id,
	seq,
	detail,
	isSelected,
	isDisabled,
	onSelect,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`grid grid-cols-[24px_32px_1fr_100px_1fr_32px] gap-1.5 py-1 px-1.5 items-center border-b border-gray-100 last:border-b-0 ${
				isDragging ? 'bg-blue-50 shadow-md rounded' : 'hover:bg-gray-50'
			}`}
		>
			{/* Drag Handle */}
			<div
				{...attributes}
				{...listeners}
				className={`flex items-center justify-center cursor-grab active:cursor-grabbing ${
					isDisabled ? 'opacity-30 pointer-events-none' : 'text-gray-400 hover:text-gray-600'
				}`}
			>
				<GripVertical className="w-3.5 h-3.5" />
			</div>

			{/* Seq */}
			<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

			{/* Detail Label */}
			<span className="text-xs text-gray-900 truncate" title={detail.detailLabel}>
				{detail.detailLabel}
			</span>

			{/* Detail Type */}
			<span className="text-xs text-gray-500 truncate">{detail.detailType}</span>

			{/* Description / Note */}
			<span className="text-xs text-gray-500 truncate" title={detail.description}>
				{detail.description}
			</span>

			{/* Select Checkbox */}
			<div className="flex items-center justify-center">
				<Checkbox checked={isSelected} onCheckedChange={checked => onSelect(id, !!checked)} disabled={isDisabled} />
			</div>
		</div>
	)
}

const BuildSiteDetailTemplateModal = ({
	details,
	openPopup,
	setOpenPopup,
	onBuildTemplate,
}: BuildSiteDetailTemplateModalProps) => {
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [selectedDetails, setSelectedDetails] = useState<Set<string>>(new Set())
	const [accessLevel, setAccessLevel] = useState('private')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [detailOrder, setDetailOrder] = useState<string[]>([])

	// Build lookup map
	const detailsById = useMemo(() => {
		const map: Record<string, DetailItem> = {}
		;(details || []).forEach(d => {
			map[d._id] = d
		})
		return map
	}, [details])

	// Initialize detail order when details change
	useEffect(() => {
		const ids = (details || []).map(d => d._id)
		setDetailOrder(ids)
	}, [details])

	// DnD sensors
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setDetailOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
		}
	}

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSelectedDetails(new Set(detailOrder))
		} else {
			setSelectedDetails(new Set())
		}
	}

	const handleDetailSelect = (id: string, checked: boolean) => {
		const next = new Set(selectedDetails)
		if (checked) {
			next.add(id)
		} else {
			next.delete(id)
		}
		setSelectedDetails(next)
	}

	const isAllSelected = detailOrder.length > 0 && detailOrder.every(id => selectedDetails.has(id))

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) newErrors.templateName = 'Template name is required'
		if (!description.trim()) newErrors.description = 'Description is required'
		if (selectedDetails.size === 0) newErrors.details = 'At least one detail must be selected'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleBuildTemplate = () => {
		if (!validate()) return

		const orderedSelectedDetails = detailOrder
			.filter(id => selectedDetails.has(id))
			.map((id, index) => {
				const d = detailsById[id]
				return {
					_id: id,
					detailLabel: d.detailLabel,
					description: d.description,
					detailType: d.detailType,
					seq: index + 1,
				}
			})

		onBuildTemplate({
			templateName: templateName.trim(),
			description: description.trim(),
			accessLevel,
			selectedDetails: orderedSelectedDetails,
		})
	}

	const isFieldsComplete = templateName.trim() && description.trim()
	const isListDisabled = !isFieldsComplete
	const isBuildDisabled = !isFieldsComplete || selectedDetails.size === 0

	// Reset form when modal closes
	useEffect(() => {
		if (!openPopup) {
			setTemplateName('')
			setDescription('')
			setSelectedDetails(new Set())
			setAccessLevel('private')
			setErrors({})
		}
	}, [openPopup])

	return (
		<div className="p-3 min-w-[37.5rem] max-w-[50rem] max-h-[46.875rem] flex flex-col gap-2">
			{/* Template Name */}
			<div className="flex flex-col gap-1">
				<div className="mb-1">
					<label htmlFor="templateName" className="block text-xs font-medium text-gray-700 mb-1">
						Template Name <span className="text-red-500">*</span>
					</label>
					<Input
						id="templateName"
						type="text"
						value={templateName}
						onChange={e => {
							setTemplateName(e.target.value)
							if (errors.templateName) setErrors({ ...errors, templateName: '' })
						}}
						className={`text-xs h-8 ${errors.templateName ? 'border-red-500' : ''}`}
						placeholder="Template Name"
					/>
					{errors.templateName && (
						<p className="text-red-500 text-[0.7rem] mt-0.5">{errors.templateName}</p>
					)}
				</div>
				<div className="mb-1">
					<label htmlFor="templateDescription" className="block text-xs font-medium text-gray-700 mb-1">
						Description <span className="text-red-500">*</span>
					</label>
					<Textarea
						id="templateDescription"
						value={description}
						onChange={e => {
							setDescription(e.target.value)
							if (errors.description) setErrors({ ...errors, description: '' })
						}}
						className={`text-xs min-h-[3.5rem] ${errors.description ? 'border-red-500' : ''}`}
						placeholder="Describe the purpose of this template"
						rows={2}
					/>
					{errors.description && (
						<p className="text-red-500 text-[0.7rem] mt-0.5">{errors.description}</p>
					)}
				</div>
			</div>

			{/* Detail List */}
			<div className="flex-1 overflow-y-auto border border-gray-300 rounded min-h-[21.875rem]">
				{/* Header */}
				<div className="grid grid-cols-[24px_32px_1fr_100px_1fr_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-300 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
					<span></span>
					<span className="text-center">Seq</span>
					<span>Label</span>
					<span>Type</span>
					<span>Note</span>
					<label className="flex items-center justify-center cursor-pointer">
						<Checkbox
							checked={isAllSelected}
							onCheckedChange={checked => handleSelectAll(!!checked)}
							disabled={isListDisabled}
						/>
					</label>
				</div>

				<div>
					{detailOrder.length === 0 ? (
						<div className="p-4 text-center text-gray-500 text-sm">No details available to build template from</div>
					) : (
						<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
							<SortableContext items={detailOrder} strategy={verticalListSortingStrategy}>
								{detailOrder.map((id, index) => {
									const detail = detailsById[id]
									if (!detail) return null
									return (
										<SortableRow
											key={id}
											id={id}
											seq={index + 1}
											detail={detail}
											isSelected={selectedDetails.has(id)}
											isDisabled={isListDisabled}
											onSelect={handleDetailSelect}
										/>
									)
								})}
							</SortableContext>
						</DndContext>
					)}
				</div>
				{errors.details && (
					<p className="text-red-500 text-[0.7rem] mt-0.5 px-3">{errors.details}</p>
				)}
			</div>

			{/* Access Level */}
			<div className="flex flex-col gap-1 p-2 bg-gray-50 rounded">
				<div className="font-semibold text-xs mb-0.5">Template Access Level</div>
				<div className="flex flex-row gap-4">
					<label className="flex items-center gap-1.5 cursor-pointer text-xs">
						<input
							type="radio"
							name="accessLevel"
							value="private"
							checked={accessLevel === 'private'}
							onChange={e => setAccessLevel(e.target.value)}
							className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
						/>
						Private
					</label>
					<label className="flex items-center gap-1.5 cursor-pointer text-xs">
						<input
							type="radio"
							name="accessLevel"
							value="org-wide"
							checked={accessLevel === 'org-wide'}
							onChange={e => setAccessLevel(e.target.value)}
							className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
						/>
						Org-Wide
					</label>
					<label className="flex items-center gap-1.5 cursor-pointer text-xs">
						<input
							type="radio"
							name="accessLevel"
							value="public"
							checked={accessLevel === 'public'}
							onChange={e => setAccessLevel(e.target.value)}
							className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
						/>
						Public
					</label>
				</div>
				<div className="text-[0.7rem] text-gray-600 mt-0.5">
					{accessLevel === 'private' && 'Only you can see and use this template.'}
					{accessLevel === 'org-wide' && 'All users in your company can see and use this template.'}
					{accessLevel === 'public' && 'All users in the system can see and use this template.'}
				</div>
			</div>

			{/* Actions */}
			<div className="flex flex-col gap-1.5 pt-1 border-t border-gray-300">
				<Button onClick={handleBuildTemplate} disabled={isBuildDisabled} className="w-full">
					Create Details Template
				</Button>

				<div className="p-2 bg-blue-50 border-l-[3px] border-blue-500 rounded text-[0.7rem] text-gray-700 leading-relaxed">
					<strong>Tip:</strong> Select the details you want to include in your template. You can drag rows to reorder them.
					Once created, this template will be available to apply to other projects.
				</div>
			</div>
		</div>
	)
}

export default BuildSiteDetailTemplateModal
